import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { ExternalLink, GraduationCap, Scissors } from "lucide-react";

const LINKS = [
  {
    id: "seminar",
    href: "https://ludbradleyclass.com",
    label: "Lud Bradley Classes",
    tagline: "Learn From a Master",
    description:
      "Intimate seminars in Black & Grey Realism for tattoo artists at all levels. Group workshops and 1-on-1 mentorship available at Avalon Tattoo Collective, New Westminster.",
    cta: "Book a Seminar",
    icon: GraduationCap,
    detail1: "2-Day Group Workshop — $950 CAD",
    detail2: "3-Day 1-on-1 Mentorship — $3,000 CAD",
    accent: true,
  },
  {
    id: "shop",
    href: "https://avalontattoocollective.ca",
    label: "Avalon Tattoo Collective",
    tagline: "Our Studio",
    description:
      "Lud's home studio in the heart of New Westminster, BC. A collective of world-class tattoo artists in a professional, welcoming environment. Come meet us.",
    cta: "Visit the Shop",
    icon: Scissors,
    detail1: "314 6th Street, New Westminster, BC",
    detail2: "By Appointment — Books Open",
    accent: false,
  },
];

export default function LinksSection() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section data-testid="links-section" ref={ref} className="py-24 md:py-32 bg-ink-950">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <p className="font-manrope text-verde text-xs tracking-[0.3em] uppercase mb-4">Ecosystem</p>
          <div className="section-divider mb-8" />
          <h2 className="font-cinzel text-4xl md:text-5xl font-bold text-[#E5E5E5] leading-tight">
            Also Find<br />Lud At
          </h2>
        </motion.div>

        {/* Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {LINKS.map((link, i) => {
            const Icon = link.icon;
            return (
              <motion.div
                key={link.id}
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: i * 0.15, duration: 0.6 }}
                data-testid={`link-card-${link.id}`}
                className={`card-hover relative bg-ink-900 border rounded-sm overflow-hidden group ${
                  link.accent ? "border-verde/30" : "border-ink-600"
                }`}
              >
                {link.accent && (
                  <div className="absolute top-0 left-0 right-0 h-0.5 bg-verde" />
                )}

                <div className="p-8 md:p-10">
                  <div className="flex items-start justify-between mb-6">
                    <div className={`p-3 rounded border ${link.accent ? "bg-verde/10 border-verde/30" : "bg-ink-800 border-ink-600"}`}>
                      <Icon size={20} className={link.accent ? "text-verde" : "text-[#A3A3A3]"} />
                    </div>
                    <ExternalLink size={14} className="text-[#404040] group-hover:text-verde transition-colors" />
                  </div>

                  <p className="font-manrope text-[10px] tracking-[0.2em] uppercase text-verde mb-2">
                    {link.tagline}
                  </p>
                  <h3 className="font-cinzel text-xl md:text-2xl font-bold text-[#E5E5E5] mb-4">
                    {link.label}
                  </h3>
                  <p className="font-manrope text-[#A3A3A3] text-sm leading-relaxed mb-6">
                    {link.description}
                  </p>

                  <div className="space-y-2 mb-8 border-t border-ink-600 pt-6">
                    <p className="font-manrope text-xs text-[#525252]">{link.detail1}</p>
                    <p className="font-manrope text-xs text-[#525252]">{link.detail2}</p>
                  </div>

                  <a
                    data-testid={`link-cta-${link.id}`}
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`inline-flex items-center gap-2 ${link.accent ? "btn-verde" : "btn-ghost"}`}
                  >
                    {link.cta}
                    <ExternalLink size={12} />
                  </a>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Studio tagline */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ delay: 0.6, duration: 0.6 }}
          className="font-manrope text-[#525252] text-xs tracking-widest uppercase text-center mt-12"
        >
          314 6th Street, New Westminster, BC &mdash; ludbradley.tattoo@gmail.com
        </motion.p>
      </div>
    </section>
  );
}
